import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./authContext";
import { useNavigate } from "react-router-dom";
import { AlertError, AlertSuccess, AlertWarning } from "./components/Alert";
import Button from "./components/Button";
import Loading from "./components/Loading";
import { FiMail } from "react-icons/fi";

type Mode = "login" | "register" | "forgot";

function mapAuthError(err: string): string {
  const e = err.toLowerCase();
  if (e.includes("email not confirmed")) return "กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ (ตรวจโฟลเดอร์ Spam ด้วย)";
  if (e.includes("user already registered")) return "อีเมลนี้ถูกใช้สมัครแล้ว กรุณาเข้าสู่ระบบ หรือกด \"ลืมรหัสผ่าน?\" หากลืมรหัสผ่าน";
  if (e.includes("invalid login credentials")) return "ข้อมูล email/password ไม่ถูกต้อง";
  if (e.includes("rate limit") || e.includes("every 60 seconds") || e.includes("too many")) return "ระบบส่งอีเมลได้จำกัด กรุณารอสักครู่แล้วลองใหม่อีกครั้ง";
  return err;
}

function Login() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("login");
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  if (session) {
    const redirect = localStorage.getItem('invite_redirect')
    if (redirect) {
      localStorage.removeItem('invite_redirect')
      navigate(redirect)
    } else {
      navigate("/onboarding");
    }
    return null;
  }

  async function loginWithGoogle() {
    const redirect = localStorage.getItem('invite_redirect')
    const redirectTo = redirect
      ? window.location.origin + redirect
      : window.location.origin + "/onboarding"
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) AlertError("Google login error: " + error.message);
  }

  async function handleResend() {
    if (!pendingEmail || resendCooldown > 0) return;
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: pendingEmail,
      options: { emailRedirectTo: window.location.origin + "/login" },
    });
    if (error) {
      AlertError(mapAuthError(error.message));
    } else {
      AlertSuccess("ส่งอีเมลยืนยันอีกครั้งแล้ว กรุณาตรวจสอบอีเมลของท่าน");
      setResendCooldown(60);
    }
    setLoading(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    if (mode === "register") {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin + "/login" },
      });
      if (error) {
        AlertError(mapAuthError(error.message));
      } else if (!data.session) {
        // Confirm email เปิดอยู่ — รอยืนยันอีเมลก่อนจึงจะเข้าสู่ระบบได้
        setPendingEmail(email);
        setResendCooldown(60);
        setPassword("");
      }
      // ถ้าได้ session ทันที (ยังไม่เปิด Confirm email) — onAuthStateChange จะ redirect ให้เอง
    } else if (mode === "forgot") {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) {
        AlertError(mapAuthError(error.message));
      } else {
        // แสดงสำเร็จเสมอแม้อีเมลไม่มีในระบบ (กันการสุ่มอีเมลผู้อื่น)
        AlertSuccess("ส่งลิงก์ตั้งรหัสผ่านใหม่เรียบร้อย กรุณาตรวจสอบอีเมลของท่าน");
        setMode("login");
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (error.message.toLowerCase().includes("email not confirmed")) {
          setPendingEmail(email);
          setResendCooldown(60);
        } else {
          AlertWarning(mapAuthError(error.message));
        }
      }
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Loading isLoading={isLoading} />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8 p-6 sm:p-8">
          <div className="flex justify-center mb-4">
            <img src="./images/lotus.jpg" alt="StockCars" className="max-h-[120px] object-contain" />
          </div>
          <h5 className="text-center text-lg font-semibold text-gray-900 mb-4">StockCars</h5>

          {pendingEmail ? (
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <FiMail className="w-10 h-10 text-primary" />
              </div>
              <p className="text-sm text-gray-700 mb-1">เราได้ส่งลิงก์ยืนยันไปที่</p>
              <p className="text-sm font-semibold text-gray-900 mb-3">{pendingEmail}</p>
              <p className="text-sm text-gray-500 mb-5">กรุณาคลิกลิงก์ในอีเมลเพื่อยืนยันอีเมลก่อนเข้าสู่ระบบ (ตรวจโฟลเดอร์ Spam ด้วย)</p>
              <Button
                variant="primary"
                size="full"
                onClick={handleResend}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? `ส่งได้อีกครั้งใน ${resendCooldown} วินาที` : "ส่งอีเมลยืนยันอีกครั้ง"}
              </Button>
              <div className="text-center mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setPendingEmail(null); setMode("login"); }}
                >
                  กลับไปหน้าเข้าสู่ระบบ
                </Button>
              </div>
            </div>
          ) : (
            <>
              {mode !== "forgot" && (
                <>
                  <Button variant="neutral" size="full" onClick={loginWithGoogle}>
                    Sign in with Google
                  </Button>

                  <hr className="my-4" />
                </>
              )}

              <form onSubmit={handleSubmit}>
                <p className="text-sm text-gray-500 mb-3">
                  {mode === "register"
                    ? "สมัครสมาชิกใหม่"
                    : mode === "forgot"
                    ? "ลืมรหัสผ่าน — กรอกอีเมลเพื่อรับลิงก์ตั้งรหัสผ่านใหม่"
                    : "หรือเข้าสู่ระบบด้วย Email"}
                </p>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {mode !== "forgot" && (
                  <>
                    <input
                      type="password"
                      placeholder="Password (6 ตัวอักษรขึ้นไป)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${mode === "login" ? "mb-2" : "mb-4"}`}
                    />
                    {mode === "login" && (
                      <div className="text-right mb-4">
                        <Button variant="ghost" size="sm" onClick={() => setMode("forgot")}>
                          ลืมรหัสผ่าน?
                        </Button>
                      </div>
                    )}
                  </>
                )}
                <Button
                  type="submit"
                  variant={mode === "register" ? "success" : mode === "forgot" ? "warning" : "primary"}
                  size="full"
                >
                  {mode === "register" ? "สมัครสมาชิก" : mode === "forgot" ? "ส่งลิงก์ตั้งรหัสผ่านใหม่" : "LOG IN"}
                </Button>
              </form>

              <div className="text-center mt-4">
                {mode === "forgot" ? (
                  <Button variant="ghost" size="sm" onClick={() => setMode("login")}>
                    กลับไปหน้าเข้าสู่ระบบ
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMode(mode === "register" ? "login" : "register")}
                  >
                    {mode === "register" ? "มีบัญชีอยู่แล้ว? เข้าสู่ระบบ" : "ยังไม่มีบัญชี? สมัครสมาชิก"}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
